import { useSearchPagesQuery } from "@/shared/services/pages/pages.query";
import { useGetFoldersQuery } from "@/shared/services/folders/folders.query";
import { useGetTemplatesQuery } from "@api/static-assets";
import {
  SectionHeader,
  useSlidesLibraryStoreShallow,
} from "@modules/slides-library/shared";
import { SlidesLibraryContentSkeleton } from "@modules/slides-library/shared/components/skeleton/slides-library-content-skeleton";
import { IconUser } from "@tabler/icons-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import type { IStoriesFolder } from "@/shared/types";
import {
  getImportStoriesCategoryLabel,
  getImportStoriesCategoryRank,
} from "../constants";
import { StoryCard } from "./story-card";
import { VersionCard } from "./version-card";
import { useSearchVersionsForImportQuery } from "@api/versions";
import { cn } from "@lib/cn";
import { StorySlidesView } from "./story-slides-view";

export const ImportFromStoriesContent = () => {
  const { source, kind, selectedStoryId } = useSlidesLibraryStoreShallow(
    (s) => ({
      source: s.importStoriesSource,
      kind: s.importStoriesKind,
      selectedStoryId: s.importStoriesSelectedStoryId,
    }),
  );

  if (selectedStoryId) {
    return <StorySlidesView />;
  }

  if (source !== "my-stories") return <TemplateStoriesContent />;
  return kind === "versions" ? <MyVersionsContent /> : <MyStoriesContent />;
};

// ── My Stories: folder-based filtering ─────────────────────

const MyStoriesContent = () => {
  const { folderId, search } = useSlidesLibraryStoreShallow((s) => ({
    folderId: s.importStoriesFolderId,
    search: s.search,
  }));

  const { data: stories, isLoading } = useSearchPagesQuery({
    folderId: folderId || undefined,
    search: search || undefined,
    status: "Draft,Live",
  });

  if (isLoading)
    return (
      <SlidesLibraryContentSkeleton variant="stories-grid" withHeader={false} />
    );

  return (
    <div>
      <ImportStoriesHeader />
      {stories && stories.length > 0 ? (
        <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-px-6 tw-pb-4">
          {stories.map((story) => (
            <StoryCard key={story._id} story={story} />
          ))}
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-6 tw-py-14">
          <p className="tw-m-0 tw-font-sans tw-text-sm tw-text-blue-500">
            No stories found
          </p>
        </div>
      )}
    </div>
  );
};

// ── My Versions: the versions of the stories in the current folder ──
//
// A version is a sent copy of a story with its own copy and pictures, and a
// slide from one is as importable as a slide from the story it came from.
// At the root every version in the org is listed; inside a folder, the
// versions of that folder's stories. The search box searches version names.
const MyVersionsContent = () => {
  const { folderId, search } = useSlidesLibraryStoreShallow((s) => ({
    folderId: s.importStoriesFolderId,
    search: s.search,
  }));
  // The folder's stories, to scope the versions to and to name them by.
  const { data: stories, isLoading: storiesLoading } = useSearchPagesQuery({
    folderId: folderId || undefined,
    status: "Draft,Live",
  });
  const pageIds = useMemo(
    () => (folderId ? (stories ?? []).map((s) => s._id) : undefined),
    [folderId, stories],
  );
  const storyTitles = useMemo(
    () =>
      new Map(
        (stories ?? []).map((s) => [
          s._id,
          (s.settings as Record<string, string> | undefined)?.title ||
            "Untitled",
        ]),
      ),
    [stories],
  );
  // A folder with no stories has no versions; do not ask for the whole org's.
  const scopeReady =
    !folderId || (!storiesLoading && (pageIds?.length ?? 0) > 0);
  // A PAGE AT A TIME. An org can hold thousands of versions; the list grows
  // by a page on "Load more" and starts over when the scope or search changes.
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [folderId, search]);
  const {
    data: versions,
    isLoading,
    isFetching,
  } = useSearchVersionsForImportQuery(
    { pageIds, search: search || undefined, page },
    { skip: !scopeReady },
  );
  if (storiesLoading || (scopeReady && isLoading))
    return (
      <SlidesLibraryContentSkeleton variant="stories-grid" withHeader={false} />
    );
  const list = scopeReady ? (versions?.items ?? []) : [];
  const hasMore = scopeReady && !!versions?.hasMore;
  return (
    <div>
      <ImportStoriesHeader />
      {list.length > 0 ? (
        <>
          <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-px-6 tw-pb-4">
            {list.map((version) => (
              <VersionCard
                key={version._id}
                version={version}
                storyTitle={storyTitles.get(version.pageId)}
              />
            ))}
          </div>
          {hasMore && (
            <div className="tw-flex tw-justify-center tw-px-6 tw-pb-6">
              <button
                type="button"
                disabled={isFetching}
                onClick={() => setPage((current) => current + 1)}
                className="tw-flex tw-h-9 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded-lg tw-border-none tw-bg-white tw-px-4 tw-font-sans tw-text-[13px] tw-font-medium tw-text-blue tw-shadow-xs tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue-100 hover:tw-bg-blue-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
              >
                {isFetching ? "Loading…" : "Load more"}
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-px-6 tw-py-14">
          <p className="tw-m-0 tw-font-sans tw-text-sm tw-text-blue-500">
            No versions found
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Stories or their versions (Figma 1023:44826): the segmented switch on the
 * right of the breadcrumbs, the same control the template step uses for its
 * sources.
 */
const ImportKindTabs = () => {
  const { kind, setKind } = useSlidesLibraryStoreShallow((s) => ({
    kind: s.importStoriesKind,
    setKind: s.setImportStoriesKind,
  }));
  return (
    <div
      role="tablist"
      aria-label="Import from"
      className="tw-flex tw-w-fit tw-shrink-0 tw-items-center tw-gap-0.5 tw-rounded-[10px] tw-bg-blue-50 tw-p-0.5"
    >
      {(
        [
          { value: "stories", label: "Stories" },
          { value: "versions", label: "Versions" },
        ] as { value: "stories" | "versions"; label: string }[]
      ).map((option) => {
        const isActive = kind === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => setKind(option.value)}
            className={cn(
              "tw-cursor-pointer tw-rounded-lg tw-border-none tw-px-3 tw-py-2 tw-font-sans tw-text-xs tw-font-medium tw-text-blue tw-transition-all",
              isActive
                ? "tw-bg-white tw-shadow-2xs tw-outline tw-outline-1 tw-outline-blue/5"
                : "tw-bg-transparent tw-text-blue/50 hover:tw-text-blue",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

/** The breadcrumbs on the left, the Stories | Versions switch on the right. */
const ImportStoriesHeader = () => (
  <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-pr-6">
    <ImportStoriesBreadcrumbs />
    <ImportKindTabs />
  </div>
);

const ImportStoriesBreadcrumbs = () => {
  const { folderId, setFolderId } = useSlidesLibraryStoreShallow((s) => ({
    folderId: s.importStoriesFolderId,
    setFolderId: s.setImportStoriesFolderId,
  }));
  const { data: foldersData } = useGetFoldersQuery();

  const path = useMemo(() => {
    if (!folderId || !foldersData) return [];
    const result: IStoriesFolder[] = [];
    let currentId: string | undefined = folderId;
    while (currentId) {
      const folder = foldersData.find((f) => f._id === currentId);
      if (!folder) break;
      result.unshift(folder);
      currentId = folder.parentId || undefined;
    }
    return result;
  }, [folderId, foldersData]);

  return (
    <div className="tw-flex tw-items-center tw-gap-2 tw-px-6 tw-py-4">
      <IconUser
        size={16}
        stroke={1}
        className="tw-shrink-0 tw-fill-none tw-text-blue-500"
      />
      <button
        type="button"
        className="tw-bg-transparent tw-shrink-0 tw-cursor-pointer tw-border-none tw-p-0 tw-font-sans tw-text-sm tw-font-normal tw-text-blue-500 tw-outline-none hover:tw-underline"
        onClick={() => setFolderId("")}
      >
        My stories
      </button>
      {path.map((folder, idx) => {
        const isLast = idx === path.length - 1;
        return (
          <Fragment key={folder._id}>
            <span className="tw-font-sans tw-text-sm tw-font-normal tw-text-blue-500">
              /
            </span>
            {isLast ? (
              <span className="tw-truncate tw-font-sans tw-text-sm tw-font-medium tw-text-blue">
                {folder.name}
              </span>
            ) : (
              <button
                type="button"
                className="tw-bg-transparent tw-shrink-0 tw-cursor-pointer tw-border-none tw-p-0 tw-font-sans tw-text-sm tw-font-normal tw-text-blue-500 tw-outline-none hover:tw-underline"
                onClick={() => setFolderId(folder._id)}
              >
                {folder.name}
              </button>
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

// ── Storydoc Templates: all categories, scroll-to navigation ──

const TemplateStoriesContent = () => {
  const { data, isLoading } = useGetTemplatesQuery();
  const search = useSlidesLibraryStoreShallow((s) => s.search);

  const templatesByCategory = useMemo(() => {
    if (!data?.templates || !data?.categories) return [];
    const query = search.trim().toLowerCase();
    return [...data.categories]
      .sort(
        (a, b) =>
          getImportStoriesCategoryRank(a.name) -
          getImportStoriesCategoryRank(b.name),
      )
      .map((cat) => ({
        name: cat.name,
        templates: data.templates!.filter(
          (t) =>
            (t.category === cat.name || cat.tags.includes(t.category)) &&
            (!query || t.title.toLowerCase().includes(query)),
        ),
      }))
      .filter((cat) => cat.templates.length > 0);
  }, [data, search]);

  if (isLoading) return <SlidesLibraryContentSkeleton variant="stories-grid" />;

  return (
    <div>
      {templatesByCategory.map((cat) => (
        <div key={cat.name} data-category-id={`category:${cat.name}`}>
          <SectionHeader label={getImportStoriesCategoryLabel(cat.name)} />
          {cat.templates.length > 0 && (
            <div className="tw-grid tw-grid-cols-2 tw-gap-2 tw-px-6 tw-pb-4">
              {cat.templates.map((template) => (
                <TemplateStoryCard
                  key={template.templateId}
                  template={template}
                />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const TemplateStoryCard = ({
  template,
}: {
  template: {
    title: string;
    thumbnail: string;
    templateId: string;
    previewUrl: string;
    contentPointer: string;
  };
}) => {
  const setImportStoriesSelectedStory = useSlidesLibraryStoreShallow(
    (s) => s.setImportStoriesSelectedStory,
  );

  const handleSelect = () =>
    setImportStoriesSelectedStory(
      template.templateId,
      template.title,
      template.contentPointer,
    );

  return (
    // biome-ignore lint/a11y/useSemanticElements: structured card with media; keep as div with role=button
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open template: ${template.title}`}
      className="tw-cursor-pointer tw-rounded-xl tw-p-2 tw-transition-colors hover:tw-bg-blue-50 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-blue-200"
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleSelect();
        }
      }}
    >
      <div className="tw-overflow-hidden tw-rounded-lg tw-shadow-sm tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue-200">
        <img
          src={template.thumbnail}
          alt={template.title}
          loading="lazy"
          decoding="async"
          className="tw-block tw-aspect-video tw-w-full tw-object-cover"
        />
      </div>
      <div className="tw-mt-3">
        <span className="tw-block tw-truncate tw-font-sans tw-text-sm tw-font-normal tw-text-blue">
          {template.title}
        </span>
      </div>
    </div>
  );
};
